import { Card, CardContent, Table, TableBody, TableContainer } from '@mui/material';
import React from 'react';
import { PaginationAndSortCommand } from '@eogile/agrements-common/src/components/table/tableTypes';
import { PaginatedListResponse } from '@eogile/agrements-common/src/api/apiTypes';
import TableCell from '@eogile/agrements-common/src/components/table/TableCell';
import TablePagination from '@eogile/agrements-common/src/components/table/TablePagination';
import FdjDemandsTableHeader from './FdjDemandsTableHeader';
import { FdjDemandSearchCommand, FdjDemandsSortableFields, FdjSimpleDemandView } from '../fdjDemandListTypes';
import { useAuthenticatedCurrentUser } from '@eogile/agrements-common/src/security/CurrentUserContext';
import { UserCompany } from '@eogile/agrements-common/src/user/commonTypes';
import { demandStatusLabels } from '@eogile/agrements-common/src/demand/demandUtils';
import { formatLocalDateForDisplay } from '@eogile/agrements-common/src/utils/dateUtils';
import dayjs from 'dayjs';
import { DemandListTableHeader } from '@eogile/agrements-common/src/demand/components/DemandListTableHeader';
import { displayAgencyName, displayLegalRepresentativeName } from '../fdjDemandUtils';
import { DemandTableRow } from '@eogile/agrements-common/src/components/table/DemandTableRow';
import DemandCommentsCell from '@eogile/agrements-common/src/demand/table/internal/DemandCommentsCell';

type DemandsTableProps = {
  apiParams: FdjDemandSearchCommand;
  data: PaginatedListResponse<FdjSimpleDemandView>;
  onCommandChange: (newCommand: PaginationAndSortCommand<FdjDemandsSortableFields>) => void;
  command: PaginationAndSortCommand<FdjDemandsSortableFields>;
};

const FdjDemandsTableCard = ({ apiParams, data, command, onCommandChange }: DemandsTableProps) => {
  const { numberOfResults, results } = data;
  const { userDetails } = useAuthenticatedCurrentUser();

  if (numberOfResults === 0) {
    return (
      <Card data-testid="table-no-result">
        <DemandListTableHeader numberOfResults={numberOfResults} apiParams={apiParams} />
      </Card>
    );
  }

  return (
    <Card>
      <DemandListTableHeader numberOfResults={numberOfResults} apiParams={apiParams} />
      <CardContent>
        <TableContainer>
          <Table size="small">
            <FdjDemandsTableHeader command={command} onCommandChange={onCommandChange} />
            <TableBody>
              {results &&
                results.map((demandDetailsView) => (
                  <DemandTableRow key={demandDetailsView.id} demand={demandDetailsView}>
                    <TableCell>{demandDetailsView.chronoNumber}</TableCell>
                    {userDetails.company === UserCompany.FDJ && (
                      <TableCell breakWord>{displayLegalRepresentativeName(demandDetailsView)}</TableCell>
                    )}
                    {userDetails.company === UserCompany.MINISTRY && (
                      <TableCell breakWord>{demandDetailsView.outletName}</TableCell>
                    )}
                    <TableCell breakWord>{demandDetailsView.outletCity}</TableCell>
                    <TableCell>{demandDetailsView.agencyRegionalDirectionName}</TableCell>
                    <TableCell>
                      {displayAgencyName(demandDetailsView.agencyCode, demandDetailsView.agencyName)}
                    </TableCell>
                    <TableCell>
                      {demandDetailsView.validationDate
                        ? formatLocalDateForDisplay(dayjs(demandDetailsView.validationDate))
                        : ''}
                    </TableCell>
                    <TableCell>
                      {demandDetailsView.receptionDate
                        ? formatLocalDateForDisplay(dayjs(demandDetailsView.receptionDate))
                        : ''}
                    </TableCell>
                    <TableCell>{demandStatusLabels[demandDetailsView.status]}</TableCell>
                    <TableCell>
                      {demandDetailsView.verdictDate
                        ? formatLocalDateForDisplay(dayjs(demandDetailsView.verdictDate))
                        : ''}
                    </TableCell>
                    <DemandCommentsCell currentUserDetails={userDetails} demandView={demandDetailsView} />
                  </DemandTableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination<FdjDemandsSortableFields>
          numberOfResults={numberOfResults}
          onCommandChange={onCommandChange}
          command={command}
        />
      </CardContent>
    </Card>
  );
};

export default FdjDemandsTableCard;
